/**
 * 3D cursor gizmo.
 *
 * Renders Blender's red/white crosshair disc at `state.cursor3D.position`.
 * The cursor is a scene-level gizmo and remains billboarded and
 * constant-screen-size to match Blender's locator-style behavior. The
 * render/inking preview workspaces mount no cursor (see `SceneGizmoLayer`
 * gating on `viewportSupportsSceneInteraction`).
 *
 * Interaction:
 *   - Tap the cursor to select it. Selection flows through
 *     `setCursor3DSelected(true)`, which is mutually exclusive with object /
 *     edit / pose selection. Once selected, the existing `TransformGizmo`
 *     targets the cursor with translation-only handles.
 *   - Hit volume is a larger invisible quad child for touch-friendliness.
 *
 * Renders at `renderOrder: 997` so it sits underneath the TransformGizmo
 * (999) when the cursor is selected and the gizmo draws over it.
 */

import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  abs,
  Discard,
  greaterThan,
  length,
  max,
  mul,
  select,
  smoothstep,
  step,
  sub,
  uv,
  vec3,
} from "three/tsl";
import * as THREE from "three/webgpu";
import { useStore } from "../../app/store.ts";
import { orientBillboardToCamera } from "../billboard.ts";
import { useDispose } from "../hooks/useDispose.ts";
import { screenToWorldRadius } from "../screenRadius.ts";
import { CURSOR3D_HIT_NAME, performCycleSelection } from "../selectionCycle.ts";
import { useTapDetection } from "../useTapDetection.ts";
import { applyGizmoRenderOrder } from "./renderOrder.ts";

const _visualGeo = new THREE.PlaneGeometry(1, 1);
const _hitGeo = new THREE.PlaneGeometry(1.6, 1.6);

const _camQuat = new THREE.Quaternion();
const _workVec = new THREE.Vector3();

const CURSOR_SCREEN_PX = 26;

function buildCursorMaterial(): THREE.MeshBasicNodeMaterial {
  const centered = sub(uv(), 0.5);
  const dist = mul(length(centered), 2.0);

  const outerRing = mul(
    smoothstep(0.84, 0.9, dist),
    smoothstep(0.98, 0.92, dist),
  );
  const innerRing = mul(
    smoothstep(0.44, 0.48, dist),
    smoothstep(0.54, 0.5, dist),
  );
  const centerDot = smoothstep(0.09, 0.0, dist);

  const cx = abs(centered.x);
  const cy = abs(centered.y);
  const armThickness = 0.012;
  const horiz = mul(step(cy, armThickness), step(cx, 0.48));
  const vert = mul(step(cx, armThickness), step(cy, 0.48));
  const crosshair = max(horiz, vert);

  const whiteMask = max(max(max(outerRing, innerRing), centerDot), crosshair);

  const quadrant = mul(centered.x, centered.y);
  const isRedQuad = greaterThan(quadrant, 0.0);
  const redRingMask = mul(outerRing, select(isRedQuad, 1.0, 0.0));

  const color = select(
    greaterThan(redRingMask, 0.01),
    vec3(1.0, 0.15, 0.25),
    vec3(1.0, 1.0, 1.0),
  );
  const alpha = max(whiteMask, mul(crosshair, 0.9));

  Discard(alpha.lessThan(0.01));

  const mat = new THREE.MeshBasicNodeMaterial();
  mat.colorNode = color;
  mat.opacityNode = alpha;
  mat.transparent = true;
  mat.depthTest = false;
  mat.depthWrite = false;
  mat.side = THREE.DoubleSide;
  return mat;
}

const _hitMaterial = new THREE.MeshBasicMaterial({
  visible: false,
  side: THREE.DoubleSide,
});

export function CursorGizmo() {
  const { camera, size } = useThree();
  const position = useStore((s) => s.cursor3D.position);
  const selected = useStore((s) => s.cursor3D.selected);

  const groupRef = useRef<THREE.Group>(null);
  const material = useMemo(buildCursorMaterial, []);
  useDispose(material);

  const { handlePointerDown, readTap } = useTapDetection();
  const handlePointerUp = useCallback(
    (e: any) => {
      const tap = readTap(e);
      if (!tap) return;
      e.stopPropagation();
      performCycleSelection(tap.clientX, tap.clientY, tap.intersections, {
        kind: "cursor",
      });
    },
    [readTap],
  );

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    applyGizmoRenderOrder(group, 997);
  });

  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;

    group.position.set(position[0], position[1], position[2]);

    const worldSize = screenToWorldRadius(
      CURSOR_SCREEN_PX,
      _workVec.set(position[0], position[1], position[2]),
      camera,
      size.height,
    );
    group.scale.setScalar(worldSize);

    orientBillboardToCamera(group, camera, _camQuat);
  });

  return (
    <group
      ref={groupRef}
      name={CURSOR3D_HIT_NAME}
      userData={{ excludeFromExport: true }}
    >
      <mesh
        geometry={_visualGeo}
        material={material}
        renderOrder={997}
        frustumCulled={false}
      />
      {selected && (
        <mesh
          geometry={_visualGeo}
          material={material}
          scale={1.35}
          renderOrder={996}
          frustumCulled={false}
        />
      )}
      <mesh
        geometry={_hitGeo}
        material={_hitMaterial}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
      />
    </group>
  );
}
